import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * A client's saved-contractor shortlist.
 *
 * Loads once per mount for clients (no-op for other roles), exposes
 * membership checks and an optimistic toggle. Server errors roll back by
 * reloading the authoritative list.
 */
const useSavedContractors = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [saved, setSaved] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!isClient) {
      setLoaded(true);
      return;
    }
    try {
      const res = await api.get('/contractors/saved/me');
      setSaved(res.data?.data || []);
    } catch (err) {
      // Non-fatal: the shortlist is progressive enhancement
      console.error('Failed to load saved contractors:', err);
    }
    setLoaded(true);
  }, [isClient]);

  useEffect(() => {
    load();
  }, [load]);

  const savedIds = useMemo(() => new Set(saved.map((c) => c.id)), [saved]);

  const isSaved = useCallback((contractorId) => savedIds.has(contractorId), [savedIds]);

  const toggle = useCallback(async (contractorId) => {
    if (!isClient || !contractorId) return;
    if (savedIds.has(contractorId)) {
      setSaved((prev) => prev.filter((c) => c.id !== contractorId));
      try {
        await api.delete(`/contractors/${contractorId}/save`);
      } catch (err) {
        console.error('Failed to unsave contractor:', err);
        load();
      }
    } else {
      setSaved((prev) => [{ id: contractorId }, ...prev]);
      try {
        await api.post(`/contractors/${contractorId}/save`);
        load();
      } catch (err) {
        console.error('Failed to save contractor:', err);
        load();
      }
    }
  }, [isClient, savedIds, load]);

  return { isClient, saved, loaded, isSaved, toggle, reload: load };
};

export default useSavedContractors;
